import React, { useState, useEffect } from "react";
import Dialog from "../../dialog/Dialog";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableCell,
  AdminTableFooter,
  AdminTableToolbar,
  TableSearchInput,
  TableEmptyRow,
  EditActionButton,
  DeleteActionButton,
  DragHandleButton,
  TableActionsCell,
  StatusBadge,
  getStatusVariant,
} from "../../common/adminTable";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as inclusionService from "./service";
import useTableSearch from "../../../hooks/useTableSearch";
import FilePreview from "../../FilePreview/FilePreview";

// Sortable row component
const SortableRow = ({
  inclusion,
  index,
  moveRow,
  handleEditClick,
  handleDeleteClick,
  handleDragEnd,
}) => {
  const ref = React.useRef(null);

  const [{ isDragging }, drag] = useDrag({
    type: "row",
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      if (monitor.didDrop()) {
        handleDragEnd();
      }
    },
  });

  const [, drop] = useDrop({
    accept: "row",
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveRow(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  drag(drop(ref));

  return (
    <tr
      ref={ref}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      className={`hover:bg-gray-50 ${
        isDragging ? "bg-gray-100 shadow-lg" : ""
      }`}
    >
      <AdminTableCell>{index + 1}</AdminTableCell>
      <AdminTableCell>
        <div className="w-7 h-7 object-cover rounded-md border-1 bg-[#F6F7FB] flex items-center justify-center">
          <img
            src={inclusion.icon_url}
            alt="Inclusion preview"
            className="h-5 w-5 object-cover rounded-md"
          />
        </div>
      </AdminTableCell>
      <AdminTableCell>{inclusion.name}</AdminTableCell>
      <AdminTableCell>
        <StatusBadge variant={getStatusVariant(inclusion.status)}>
          {inclusion.status === 1 ? "Active" : "In-Active"}
        </StatusBadge>
      </AdminTableCell>
      <TableActionsCell>
        <EditActionButton
          showLabel={false}
          onClick={() => handleEditClick(inclusion.id)}
        />
        <DeleteActionButton
          showLabel={false}
          onClick={() => handleDeleteClick(inclusion)}
        />
        <DragHandleButton isDragging={isDragging} />
      </TableActionsCell>
    </tr>
  );
};

const Inclusions = () => {
  const queryClient = useQueryClient();

  // State management
  const [selectedInclusion, setSelectedInclusion] = useState(null);
  const { searchQuery, setSearchQuery, activeSearch, clearSearch } =
    useTableSearch();
  const [inclusionName, setInclusionName] = useState("");
  const [inclusionStatus, setInclusionStatus] = useState("1");
  const [iconImage, setIconImage] = useState(null);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [orderedInclusions, setOrderedInclusions] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [errors, setErrors] = useState({
    name: "",
    icon: "",
  });

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  // Queries
  const { data: inclusionData } = useQuery({
    queryKey: ["inclusion-master", currentPage, activeSearch],
    queryFn: () =>
      inclusionService.list({ page: currentPage, search: activeSearch || undefined }),
    keepPreviousData: true,
  });

  // Update orderedInclusions when inclusions change
  useEffect(() => {
    if (inclusionData?.data?.data) {
      setOrderedInclusions(inclusionData.data.data);
    }
  }, [inclusionData]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (formData) => inclusionService.create(formData),
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(
        success.response?.data?.message || "Inclusion added successfully!"
      );
      setAddDialogOpen(false);
      resetForm();
      queryClient.invalidateQueries(["inclusion-master"]);
      queryClient.invalidateQueries(["inclusion-master-list"]);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(
        error.response?.data?.message || "Failed to add inclusion"
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, formData }) =>
      inclusionService.update({ id, inclusionData: formData }),
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(
        success.response?.data?.message || "Inclusion updated successfully!"
      );
      setEditDialogOpen(false);
      resetForm();
      queryClient.invalidateQueries(["inclusion-master"]);
      queryClient.invalidateQueries(["inclusion-master-list"]);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(
        error.response?.data?.message || "Failed to update inclusion"
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: inclusionService.deleteInclusion,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(
        success.response?.data?.message || "Inclusion deleted successfully!"
      );
      setDeleteDialogOpen(false);
      queryClient.invalidateQueries(["inclusion-master"]);
      queryClient.invalidateQueries(["inclusion-master-list"]);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(
        error.response?.data?.message || "Failed to delete inclusion"
      );
    },
  });

  const getByIdMutation = useMutation({
    mutationFn: inclusionService.getById,
    onSuccess: (data) => {
      setIsLoading(false);
      setSelectedInclusion(data.data);
      setInclusionName(data.data.name);
      setInclusionStatus(data.data.status.toString());
      setIconImage(null);
      setEditDialogOpen(true);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(
        error.response?.data?.message || "Failed to fetch inclusion details"
      );
    },
  });

  const updateOrderMutation = useMutation({
    mutationFn: inclusionService.updateOrder,
    onSuccess: (success) => {
      showSuccessMessage(
        success.response?.data?.message ||
          "Inclusion order updated successfully!"
      );
      queryClient.invalidateQueries(["inclusion-master"]);
      queryClient.invalidateQueries(["inclusion-master-list"]);
    },
    onError: (error) => {
      showErrorMessage(
        error.response?.data?.message || "Failed to update inclusion order"
      );
      // Revert the order on error
      if (inclusionData?.data?.data) {
        setOrderedInclusions(inclusionData.data.data);
      }
    },
  });

  //// Add sort mutation (same as states)
  const sortMutation = useMutation({
    mutationFn: inclusionService.sort,
    onSuccess: (success) => {
      showSuccessMessage(
        success.response?.data?.message || "Inclusions reordered successfully!"
      );
      queryClient.invalidateQueries(["inclusion-master"]);
      queryClient.invalidateQueries(["inclusion-master-list"]);
    },
    onError: (error) => {
      showErrorMessage(
        error.response?.data?.message || "Failed to reorder inclusions"
      );
      setShowError(true);
    },
  });

  // Helper functions
  const resetForm = () => {
    setInclusionName("");
    setInclusionStatus("1");
    setIconImage(null);
    setErrors({
      name: "",
      icon: "",
    });
  };

  const validateForm = () => {
    let tempErrors = {
      name: "",
      icon: "",
    };
    let isValid = true;

    if (!inclusionName.trim()) {
      tempErrors.name = "Inclusion name is required";
      isValid = false;
    }

    if (!iconImage && !selectedInclusion) {
      tempErrors.icon = "Icon image is required";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage("");
    }, 3000);
  };

  const showErrorMessage = (errorMsg) => {
    setShowError(true);
    setErrorMessage(errorMsg);
    setTimeout(() => {
      setShowError(false);
      setErrorMessage("");
    }, 3000);
  };

  // Action handlers
  const handleAddClick = () => {
    resetForm();
    setAddDialogOpen(true);
  };

  const handleDeleteClick = (inclusion) => {
    setSelectedInclusion(inclusion);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (inclusionId) => {
    getByIdMutation.mutate(inclusionId);
  };

  const handleSaveInclusion = () => {
    setIsLoading(true);
    if (validateForm()) {
      const formData = new FormData();
      formData.append("name", inclusionName);
      formData.append("status", inclusionStatus);
      if (iconImage) {
        formData.append("icon", iconImage);
      }

      createMutation.mutate(formData);
    } else {
      setIsLoading(false);
    }
  };

  const handleUpdateInclusion = () => {
    setIsLoading(true);
    if (validateForm()) {
      const formData = new FormData();
      formData.append("name", inclusionName);
      formData.append("status", inclusionStatus);
      if (iconImage) {
        formData.append("icon", iconImage);
      }

      updateMutation.mutate({
        id: selectedInclusion.id,
        formData,
      });
    } else {
      setIsLoading(false);
    }
  };

  const handleDeleteInclusion = () => {
    setIsLoading(true);
    if (selectedInclusion) {
      deleteMutation.mutate(selectedInclusion.id);
    }
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const moveRow = (dragIndex, hoverIndex) => {
    setDragIndex(dragIndex);
    setHoverIndex(hoverIndex);
    const draggedInclusion = orderedInclusions[dragIndex];
    const updatedInclusions = [...orderedInclusions];
    updatedInclusions.splice(dragIndex, 1);
    updatedInclusions.splice(hoverIndex, 0, draggedInclusion);
    setOrderedInclusions(updatedInclusions);
  };

  const handleDragEnd = () => {
    if (dragIndex !== null && hoverIndex !== null) {
      const sort_from = orderedInclusions[dragIndex]?.id;
      const sort_to = orderedInclusions[hoverIndex]?.id;
      if (sort_from && sort_to) {
        sortMutation.mutate({ sort_from, sort_to });
      }
    }
    setDragIndex(null);
    setHoverIndex(null);
  };

  const pagination = {
    currentPage: inclusionData?.current_page || 1,
    lastPage: inclusionData?.last_page || 1,
    perPage: inclusionData?.per_page || 10,
    total: inclusionData?.total || 0,
    from: inclusionData?.from || 0,
    to: inclusionData?.to || 0,
  };

  return (
    <AdminPageLayout>
      {/* Error Toster */}
      {showError && (
        <Toster
          message={errorMessage}
          type="error"
          duration={3000}
          onClose={() => setShowError(false)}
        />
      )}

      {/* Success Toster */}
      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => {
            setShowSuccess(false);
            setSuccessMessage("");
          }}
        />
      )}

      <AdminPageHeader
        title={ADMIN_PAGE_META.inclusions.title}
        subtitle={ADMIN_PAGE_META.inclusions.subtitle}
      />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <AdminTableToolbar className="mb-0">
            <TableSearchInput
              placeholder="Search inclusions..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <button
              onClick={handleAddClick}
              className="bg-primary text-white px-4 py-1 rounded-lg border border-primary shadow-md hover:bg-primary/90 transition-all"
            >
              Add Inclusion
            </button>
          </AdminTableToolbar>
        </AdminPageCardToolbar>

        <AdminTable
          className="shadow-none rounded-none"
          footer={
            <AdminTableFooter>
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
                showInfo={true}
              />
            </AdminTableFooter>
          }
        >
        <DndProvider backend={HTML5Backend}>
          <AdminTableHead>
            <AdminTableTh>#</AdminTableTh>
            <AdminTableTh>Icon</AdminTableTh>
            <AdminTableTh>Title</AdminTableTh>
            <AdminTableTh>Status</AdminTableTh>
            <AdminTableTh align="right">Actions</AdminTableTh>
          </AdminTableHead>
          <AdminTableBody>
            {orderedInclusions.length > 0 ? (
              orderedInclusions.map((inclusion, index) => (
                <SortableRow
                  key={inclusion.id}
                  inclusion={inclusion}
                  index={index}
                  moveRow={moveRow}
                  handleEditClick={handleEditClick}
                  handleDeleteClick={handleDeleteClick}
                  handleDragEnd={handleDragEnd}
                />
              ))
            ) : (
              <TableEmptyRow
                colSpan={5}
                emptyMessage="No inclusions found"
                searchQuery={activeSearch}
                onClearSearch={clearSearch}
              />
            )}
          </AdminTableBody>
        </DndProvider>
      </AdminTable>
      </AdminPageCard>

      {/* Add Inclusion Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setErrors({ name: "", icon: "" });
        }}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-bs-add"></i>
            Create Inclusion
          </div>
        }
        primaryButtonText="Add Inclusion"
        width="w-[500px]"
        primaryButtonAction={handleSaveInclusion}
        closeButtonText="Cancel"
        closeButtonAction={() => {
          setAddDialogOpen(false);
          setErrors({ name: "", icon: "" });
        }}
        isLoading={isLoading}
      >
        <div className="grid grid-cols-2 gap-2">
          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Inclusion Name
            </label>
            <input
              type="text"
              className={`text-sm w-full px-3 py-1 border ${
                errors.name ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary`}
              placeholder="Enter Inclusion name"
              value={inclusionName}
              onChange={(e) => {
                setInclusionName(e.target.value);
                setErrors({ ...errors, name: "" });
              }}
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>

          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Icon Image
            </label>
            <input
              type="file"
              accept="image/*"
              className={`text-sm w-full px-2 py-1 border ${
                errors.icon ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary`}
              onChange={(e) => {
                setIconImage(e.target.files[0]);
                setErrors({ ...errors, icon: "" });
              }}
            />
            {errors.icon && (
              <p className="text-red-500 text-xs mt-1">{errors.icon}</p>
            )}
          </div>

          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              className="text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              value={inclusionStatus}
              onChange={(e) => setInclusionStatus(e.target.value)}
            >
              <option value="1">Active</option>
              <option value="0">In-Active</option>
            </select>
          </div>
        </div>

        {/* Preview Section */}
        <div className="mt-4">
          <label className="block font-bold text-sm text-gray-700 mb-1">
            Attachment preview
          </label>
          {iconImage && (
            <FilePreview
              file={iconImage}
              label="Icon Image"
              onDelete={() => setIconImage(null)}
              isImage={true}
            />
          )}
        </div>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-pencil text-[16px]"></i>
            Edit Inclusion
          </div>
        }
        primaryButtonText="Update Inclusion"
        primaryButtonAction={handleUpdateInclusion}
        closeButtonText="Cancel"
        width="w-[500px]"
        closeButtonAction={() => setEditDialogOpen(false)}
        isLoading={isLoading}
      >
        <div className="grid grid-cols-2 gap-2">
          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Inclusion Name
            </label>
            <input
              type="text"
              className="text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              placeholder="Enter Inclusion name"
              value={inclusionName}
              onChange={(e) => setInclusionName(e.target.value)}
            />
          </div>

          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              className="text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              value={inclusionStatus}
              onChange={(e) => setInclusionStatus(e.target.value)}
            >
              <option value="1">Active</option>
              <option value="0">In-Active</option>
            </select>
          </div>

          <div className="mb-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Icon Image
            </label>
            <input
              type="file"
              accept="image/*"
              className="text-sm w-full px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              onChange={(e) => setIconImage(e.target.files[0])}
            />
          </div>
        </div>

        {/* Preview Section */}
        <div className="mt-4">
          <label className="block font-bold text-sm text-gray-700 mb-1">
            Attachment preview
          </label>
          {iconImage ? (
            <FilePreview
              file={iconImage}
              label="Icon Image"
              onDelete={() => setIconImage(null)}
              isImage={true}
            />
          ) : selectedInclusion?.icon_url ? (
            <FilePreview
              file={selectedInclusion.icon_url}
              label="Current Icon Image"
              isImage={true}
            />
          ) : (
            <div className="text-sm text-gray-500">No icon image selected</div>
          )}
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-trash text-[16px]"></i>
            Delete Inclusion
          </div>
        }
        primaryButtonText="Delete"
        primaryButtonAction={handleDeleteInclusion}
        closeButtonText="Cancel"
        closeButtonAction={() => setDeleteDialogOpen(false)}
        isLoading={isLoading}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete this inclusion? This action cannot
            be undone.
          </p>
        </div>
      </Dialog>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg shadow-lg">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      )}
    </AdminPageLayout>
  );
};

export default Inclusions;
